import { useContext, useEffect, useState } from "react";
import { Button, Col, Row, Table, Form } from "react-bootstrap";
import "../../assets/css/vendors/HotelScreens/hotelEarning.css";
import HotelStatCard from "../../components/vendors/HotelStatCard";
import VendorLayout from "../../layout/HotelVendorLayout/VendorLayout";
import AppContext from "../../context/AppContext";
import axios from "axios";

const HotelEarning = () => {
  const { baseUrl, authToken, hotelId } = useContext(AppContext);

  const getVendorId = () => {
    try {
      const payload = JSON.parse(atob(authToken.split(".")[1]));
      return payload.sub;
    } catch (e) {
      return localStorage.getItem("vendorId");
    }
  };
  const vendorId = getVendorId();

  const [years, setYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [yearlyTotals, setYearlyTotals] = useState({});
  const [monthlyBreakdown, setMonthlyBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentYear = new Date().getFullYear();
  const currentMonthName = new Date().toLocaleString("en-US", { month: "long" });

  const fetchEarnings = (year) => {
    if (!vendorId || !hotelId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    axios
      .get(`${baseUrl}/vendor/hotel-earnings`, {
        params: { jjvendor_id: vendorId, hotel_id: hotelId, year },
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        if (res.data.success) {
          setYears(res.data.years || []);
          setSelectedYear(res.data.selected_year);
          setYearlyTotals(res.data.yearly_totals || {});
          setMonthlyBreakdown(res.data.monthly_breakdown || []);
        }
      })
      .catch((err) => {
        console.error("Earnings fetch error:", err);
        setYearlyTotals({});
        setMonthlyBreakdown([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEarnings(selectedYear);
  }, [vendorId, hotelId]);

  const handleYearChange = (e) => {
    const year = e.target.value;
    setSelectedYear(year);
    fetchEarnings(year);
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "Rs. 0";
    return `Rs. ${Number(amount).toLocaleString("en-IN")}`;
  };

  if (loading) {
    return (
      <VendorLayout>
        <div className="text-center py-5">Loading earnings...</div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="hsc-earnings-container">
        {/* Header */}
        <div className="hsc-earnings-header">
          <div>
            <h3 className="hsc-earnings-title">Earnings Dashboard</h3>
            <p className="hsc-earnings-subtitle">Yearly revenue overview</p>
          </div>
          <div className="hsc-header-actions">
            <Form.Select
              value={selectedYear}
              onChange={handleYearChange}
              className="hsc-year-select"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Form.Select>
          </div>
        </div>
        <Row className="mb-4">
          <Col xl={3} lg={6} className="mb-4">
            <HotelStatCard
              title={`Revenue - ${selectedYear}`}
              value={yearlyTotals.total_revenue || 0}
              icon="₹"
              badgeText={`${selectedYear}`}
              cardClass="hsc-revenue-card"
              iconBg="hsc-bg-gradient-success"
              iconColor="text-white"
            />
          </Col>

          <Col xl={3} lg={6} className="mb-4">
            <HotelStatCard
              title={`Net Earning - ${selectedYear}`}
              value={yearlyTotals.net_earning || 0}
              icon="💰"
              badgeText={`${selectedYear}`}
              cardClass="hsc-profit-card"
              iconBg="hsc-bg-gradient-primary"
              iconColor="text-white"
            />
          </Col>

          <Col xl={3} lg={6} className="mb-4">
            <HotelStatCard
              title="Platform Service Charges (10%)"
              value={yearlyTotals.platform_charges || 0}
              icon="📊"
              badgeText={`${selectedYear}`}
              cardClass="hsc-expense-card"
              iconBg="hsc-bg-gradient-warning"
              iconColor="text-white"
            />
          </Col>

          <Col xl={3} lg={6} className="mb-4">
            <HotelStatCard
              title="Avg. Revenue Per Month"
              value={yearlyTotals.avg_revenue_per_month || 0}
              icon="📈"
              badgeText={`${selectedYear}`}
              cardClass="hsc-avg-card"
              iconBg="hsc-bg-gradient-info"
              iconColor="text-white"
            />
          </Col>
        </Row>
        <Row>
          <Col lg={12}>
            <div className="hsc-dashboard-section">
              <div className="hsc-section-header">
                <h5>Monthly Breakdown ({selectedYear})</h5>
                <p className="text-muted">Revenue, platform charges and net earning by month</p>
              </div>
              <div className="table-responsive">
                <Table hover className="hsc-monthly-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th className="text-end">Bookings</th>
                      <th className="text-end">Revenue</th>
                      <th className="text-end">Platform Charges (10%)</th>
                      <th className="text-end">Net Earning</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyBreakdown.map((item, index) => {
                      const isCurrentMonth =
                        Number(selectedYear) === currentYear && item.month === currentMonthName;

                      return (
                        <tr
                          key={index}
                          className={isCurrentMonth ? "hsc-selected-month-row" : ""}
                        >
                          <td>
                            {item.month}
                            {isCurrentMonth && (
                              <span className="ms-2 hsc-hotel-current-badge">Current</span>
                            )}
                          </td>
                          <td className="text-end">{item.bookings_count}</td>
                          <td className="text-end">
                            <div className="hsc-revenue-amount">{formatCurrency(item.revenue)}</div>
                          </td>
                          <td className="text-end">
                            <div className="text-muted">{formatCurrency(item.platform_charges)}</div>
                          </td>
                          <td className="text-end">
                            <div className="hsc-profit-amount">{formatCurrency(item.net_earning)}</div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {monthlyBreakdown.every((m) => m.bookings_count === 0) && (
                <p className="text-muted text-center mt-3">No earnings recorded for {selectedYear}.</p>
              )}
            </div>
          </Col>
        </Row>

      </div>
    </VendorLayout>
  );
};

export default HotelEarning;